// Static content for the marketing /pricing page.
// The page is intentionally minimal: products (free + pay-per-token) and
// collaboration/support plans. No comparison table, no FAQ.

export type ProductTag = "free" | "meter";

export type PriceRow = {
  /** Bold, foreground number (omit for a muted-only note like "Same as Cloud inference"). */
  num?: string;
  /** Muted unit / note text. */
  unit: string;
};

export type ProductDetailLine = {
  label: string;
  rows: PriceRow[];
};

export type Product = {
  id: string;
  name: string;
  tag: ProductTag;
  copy: string;
  /** Optional CTA, pinned to the bottom of the card. Renders as an outline
   *  button by default, or a text link when variant is "link". */
  cta?: { text: string; href: string; variant?: "button" | "link" };
  /** Optional install command rendered as a small terminal prompt. */
  install?: string;
  /** Optional direct-download links (e.g. model weights on HuggingFace). */
  downloads?: { label: string; href: string }[];
  detail?: ProductDetailLine[];
};

export type PlanCta = {
  text: string;
  /** "ink" renders the inverse/foreground button used by Enterprise. */
  variant: "primary" | "outline" | "ink";
  /** Internal route, or omit to open the contact dialog. */
  href?: string;
};

export type PlanFeature = {
  label: string;
  /** Renders the line in bold/foreground (used for the headline seat count). */
  strong?: boolean;
};

export type Plan = {
  id: string;
  name: string;
  /** Display amount, e.g. "$0", "$350", "Custom". */
  amount: string;
  /** Per-unit suffix, e.g. "/ month" (omit for Custom). */
  per?: string;
  /** Secondary billing note; supports **bold** spans. */
  annualNote?: string;
  /** "Best for …" line; supports **bold** spans. */
  bestFor: string;
  cta: PlanCta;
  features: PlanFeature[];
  featured?: boolean;
  /** Small note rendered under the card (Team only). */
  seatNote?: string;
};

export const products: Product[] = [
  {
    id: "photon",
    name: "Photon",
    tag: "free",
    copy: "The world's fastest VLM inference engine — runs everywhere, from edge to server-class hardware.",
    install: "npm install moondream",
    cta: { text: "Learn more about Photon", href: "/photon", variant: "link" },
  },
  {
    id: "models",
    name: "Models",
    tag: "free",
    copy: "Open and free to use — hosting them as a cloud service requires our license.",
    downloads: [
      { label: "Moondream 2", href: "https://huggingface.co/vikhyatk/moondream2" },
      {
        label: "Moondream 3 (preview)",
        href: "https://huggingface.co/moondream/moondream3-preview",
      },
    ],
  },
  {
    id: "cloud",
    name: "Cloud",
    tag: "meter",
    copy: "Hosted Moondream inference — lightning fast, always on, with HIPAA and SOC 2 available.",
    cta: { text: "Start building", href: "/cloud" },
    detail: [
      {
        label: "Moondream 3.1 9B A2B — Real-time",
        rows: [
          { num: "$0.30", unit: "/ 1M input tokens" },
          { num: "$1.00", unit: "/ 1M output tokens" },
        ],
      },
      {
        label: "Moondream 3.1 9B A2B — Batch",
        rows: [
          { num: "$0.15", unit: "/ 1M input tokens" },
          { num: "$0.50", unit: "/ 1M output tokens" },
        ],
      },
      {
        label: "Moondream 3 Preview — Real-time",
        rows: [
          { num: "$0.30", unit: "/ 1M input tokens" },
          { num: "$2.50", unit: "/ 1M output tokens" },
        ],
      },
      {
        label: "Moondream 3 Preview — Batch",
        rows: [
          { num: "$0.15", unit: "/ 1M input tokens" },
          { num: "$1.25", unit: "/ 1M output tokens" },
        ],
      },
    ],
  },
  {
    id: "lens",
    name: "Lens",
    tag: "meter",
    copy: "Fine-tune Moondream for your task.",
    cta: { text: "Fine-tune a model", href: "/cloud" },
    detail: [
      {
        label: "Rollouts",
        rows: [{ unit: "Same as Cloud inference" }],
      },
      {
        label: "Training",
        rows: [{ num: "$0.60", unit: "/ 1M training tokens" }],
      },
    ],
  },
];

export const plans: Plan[] = [
  {
    id: "free",
    name: "Free",
    amount: "$0",
    per: "/ month",
    bestFor: "Best for **individual developers.**",
    cta: { text: "Start free", variant: "outline", href: "/login" },
    features: [
      { label: "1 seat" },
      { label: "Personal workspace" },
      { label: "Discord support" },
      { label: "$5/month usage credits" },
    ],
  },
  {
    id: "team",
    name: "Team",
    amount: "$350",
    per: "/ month",
    annualNote: "or **$300**/mo billed annually",
    bestFor: "Best for **teams building together.**",
    cta: { text: "Start Team", variant: "primary", href: "/org/me/create-organization" },
    featured: true,
    seatNote: "Additional seats available. Contact us.",
    features: [
      { label: "10 seats", strong: true },
      { label: "Shared fine-tunes" },
      { label: "Shared projects" },
      { label: "Shared API keys" },
      { label: "Shared billing" },
      { label: "Dedicated Slack" },
      { label: "1h/month consulting" },
      { label: "$5/month usage credits" },
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    amount: "Custom",
    bestFor: "Best for **larger, regulated, or custom deployments.**",
    cta: { text: "Talk to sales", variant: "ink" },
    features: [
      { label: "Custom seats", strong: true },
      { label: "HIPAA / BAA available" },
      { label: "Security review" },
      { label: "Priority support" },
      { label: "Custom deployment support" },
      { label: "Custom billing" },
    ],
  },
];

export const goodToKnow: string[] = [
  "Every workspace includes **$5/month usage credits**.",
  "Batch and fine-tune (LoRA) multipliers are the **same for every model**.",
  "Fine-tuned models use the **same inference pricing** as base models.",
  "Fine-tunes are **unlimited**. Usage and rate limits still apply.",
  "Paid plans are for **collaboration and support**, not cheaper tokens.",
];
